// ✅ Ana səhifə — Rəsmi Top-Up və E-Pin (premium xidmət showcase).
// Orta ölçülü landşaft «service tile»-lar + üfüqi karusel. Məhsul kartı deyil —
// rəsmi xidmət qısayolları. Feed boşdursa köhnə kateqoriyalara geri düşür.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FaCheckCircle, FaBolt, FaGamepad, FaGift, FaTv, FaCode, FaShieldAlt, FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import HomeSection from './HomeSection'
import { SectionHeader } from '../ui'
import { get_official_categories, get_official_home_services } from '../../store/reducers/digitalProductReducer'
import { useOfficialSectionSettings } from '../../hooks/useOfficialSectionSettings'

// serviceType → tab qrupu
const TYPE_GROUP = {
    top_up: 'topup', id_topup: 'topup',
    gift_card: 'gift',
    subscription: 'subscription', software: 'subscription', account: 'subscription', account_info: 'subscription',
    e_pin_code: 'code', code: 'code', random_key: 'code',
}
const TABS = [
    ['all', 'Hamısı'], ['topup', 'Top-Up'], ['gift', 'Gift Card'], ['subscription', 'Abunəlik'], ['code', 'Kod / E-Pin'],
]
const TYPE_LABEL = { topup: 'Top-Up', gift: 'Gift Card', subscription: 'Abunəlik', code: 'Kod / E-Pin', other: 'Rəsmi' }
const TYPE_SUBTITLE = {
    topup: 'Top-Up xidmətləri', gift: 'Gift card paketləri', subscription: 'Abunəlik paketləri',
    code: 'Kod / E-Pin paketləri', other: 'Rəsmi xidmət',
}
const TYPE_ICON = { topup: <FaGamepad />, gift: <FaGift />, subscription: <FaTv />, code: <FaCode />, other: <FaBolt /> }
// Tipə görə incə aksent (yalnız ikon + çip — kart özü açıq/təmiz qalır)
const TYPE_ACCENT = {
    topup:        { grad: 'from-violet-500 to-indigo-600', chip: 'bg-brand-50 text-brand-700' },
    gift:         { grad: 'from-rose-500 to-orange-500',   chip: 'bg-rose-50 text-rose-700' },
    subscription: { grad: 'from-sky-500 to-cyan-500',      chip: 'bg-sky-50 text-sky-700' },
    code:         { grad: 'from-emerald-500 to-teal-600',  chip: 'bg-emerald-50 text-emerald-700' },
    other:        { grad: 'from-slate-500 to-slate-700',   chip: 'bg-slate-100 text-slate-600' },
}

// Orta ölçülü premium xidmət tile-ı (banner/qiymət/stok yox)
const ServiceTile = ({ item }) => {
    const { group, to, title, media, count, comingSoon } = item
    const a = TYPE_ACCENT[group] || TYPE_ACCENT.other
    return (
        <Link to={to}
            // Genişlik inline clamp ilə: geniş ekranda dəqiq 4 sıra (25% − boşluq),
            // dar ekranda min 216px → üfüqi sürüşdürmə. Tailwind max-width variant
            // sırası bu arbitrary genişliklərdə etibarsızdır, ona görə inline.
            style={{ width: 'clamp(216px, calc(25% - 9px), 360px)' }}
            className='group relative flex shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-300 hover:shadow-[0_16px_34px_rgba(124,58,237,0.16)]'>
            {/* dekorativ qradiyent blob (küncdə, çox incə) */}
            <div className={`pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-gradient-to-br ${a.grad} opacity-[0.08] blur-2xl transition-opacity duration-300 group-hover:opacity-[0.16]`} aria-hidden='true' />

            {/* üst: rəsmi rozet + kateqoriya çipi */}
            <div className='relative flex items-center justify-between gap-2'>
                <span className='inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-700'>
                    <FaCheckCircle size={8} /> Rəsmi
                </span>
                <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold ${a.chip}`}>{TYPE_LABEL[group]}</span>
            </div>

            {/* əsas: ikon + ad/tip (landşaft) */}
            <div className='relative mt-2.5 flex items-center gap-3'>
                <div className='relative shrink-0'>
                    {media ? (
                        <div className='flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white ring-1 ring-slate-200 shadow-sm transition-all duration-300 group-hover:ring-brand-300'>
                            <img src={media} alt={title} loading='lazy' decoding='async'
                                onError={(e) => { e.currentTarget.style.display = 'none' }}
                                className='h-full w-full object-contain p-1.5 transition-transform duration-300 group-hover:scale-105' />
                        </div>
                    ) : (
                        <div className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${a.grad} text-lg text-white shadow-sm transition-transform duration-300 group-hover:scale-105`}>
                            {TYPE_ICON[group]}
                        </div>
                    )}
                    {/* yumşaq işıq (hover-də) */}
                    <div className={`pointer-events-none absolute inset-0 rounded-full bg-gradient-to-br ${a.grad} opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-25`} aria-hidden='true' />
                </div>
                <div className='min-w-0 flex-1'>
                    <h3 className='truncate text-[13px] font-bold leading-tight text-slate-900 transition-colors group-hover:text-brand-700'>{title}</h3>
                    <p className='mt-0.5 truncate text-[11px] text-slate-500'>{TYPE_SUBTITLE[group]}</p>
                </div>
            </div>

            {/* alt: paket sayı + CTA işarəsi */}
            <div className='relative mt-auto flex items-center justify-between gap-2 pt-3'>
                <span className='text-[10px] font-semibold text-slate-400'>{comingSoon ? 'Tezliklə' : `${count} paket`}</span>
                <span className='inline-flex items-center gap-1 text-[11px] font-bold text-brand-600'>
                    Paketlərə bax <FaArrowRight size={8} className='transition-transform duration-300 group-hover:translate-x-0.5' />
                </span>
            </div>
        </Link>
    )
}

const OfficialTopUpSection = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { officialCategories, officialHomeServices } = useSelector(s => s.digitalProduct)
    const cfg = useOfficialSectionSettings()
    const [tab, setTab] = useState('all')
    const railRef = useRef(null)
    const [canPrev, setCanPrev] = useState(false)
    const [canNext, setCanNext] = useState(false)

    useEffect(() => {
        dispatch(get_official_home_services())
        if (!officialCategories || officialCategories.length === 0) dispatch(get_official_categories())
    }, [dispatch])  // eslint-disable-line react-hooks/exhaustive-deps

    const services = officialHomeServices || []

    // Xidmət feed-i → tile; boşdursa köhnə kateqoriyalara geri düş.
    const allItems = useMemo(() => {
        if (services.length > 0) {
            return services.map(svc => ({
                key: svc.id,
                group: TYPE_GROUP[svc.serviceType] || 'other',
                to: svc.slug === '_default' ? `/official/${svc.categorySlug}` : `/official/${svc.categorySlug}/${svc.slug}`,
                title: svc.title,
                media: svc.image || svc.icon || svc.banner,
                count: svc.packageCount,
                comingSoon: !(svc.packageCount > 0),
            }))
        }
        return (officialCategories || []).map(cat => ({
            key: cat._id,
            group: { game: 'topup', gift_card: 'gift', subscription: 'subscription', software: 'subscription' }[cat.type] || 'other',
            to: `/official/${cat.slug}`,
            title: cat.title,
            media: cat.icon || cat.banner,
            count: cat.packageCount || 0,
            comingSoon: !((cat.packageCount || 0) > 0),
        }))
    }, [services, officialCategories])

    const availableTabs = useMemo(() => {
        const present = new Set(allItems.map(c => c.group))
        return TABS.filter(([k]) => k === 'all' || present.has(k))
    }, [allItems])

    const items = useMemo(
        () => (tab === 'all' ? allItems : allItems.filter(c => c.group === tab)).slice(0, Math.max(1, cfg.maxItems || 4)),
        [allItems, tab, cfg.maxItems]
    )

    const updateRail = () => {
        const rail = railRef.current
        if (!rail) return
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth)
        setCanPrev(rail.scrollLeft > 4)
        setCanNext(rail.scrollLeft < max - 4)
    }

    useEffect(() => {
        const rail = railRef.current
        if (!rail) return undefined
        rail.scrollTo({ left: 0, behavior: 'smooth' })
        const frame = requestAnimationFrame(updateRail)
        const observer = new ResizeObserver(updateRail)
        observer.observe(rail)
        return () => { cancelAnimationFrame(frame); observer.disconnect() }
    }, [tab, items.length])

    const scroll = (dir) => {
        const rail = railRef.current
        if (!rail) return
        rail.scrollBy({ left: dir * Math.max(rail.clientWidth * 0.8, 280), behavior: 'smooth' })
    }

    const scrollable = canPrev || canNext

    // ── Admin idarəsi (Stage 2) ──
    // Bölmə söndürülübsə heç render olunmur.
    if (!cfg.enabled) return null
    // Data yoxdur + hideIfEmpty → gizlə. hideIfEmpty=false → kompakt boş-hal.
    if (allItems.length === 0 && cfg.hideIfEmpty) return null

    return (
        <HomeSection card tone='brand' className='scroll-mt-24'>
            {/* dekorativ fon blobu (premium, incə). overflow-x:clip blobun sağ
                kənardan daşmasını kəsir (üfüqi scroll yaranmır), şaquli hover
                qalxması/kölgəsi görünür qalır. */}
            <div className='relative' style={{ overflowX: 'clip' }}>
                <div className='pointer-events-none absolute right-0 -top-8 h-40 w-40 rounded-full bg-brand-200/40 blur-3xl' aria-hidden='true' />

                <div className='relative'>
                    <SectionHeader
                        eyebrow={{ icon: <FaShieldAlt />, label: cfg.chipText || 'ITEMZO RƏSMİ', tone: 'brand' }}
                        title={cfg.title || 'Rəsmi Top-Up və E-Pin'}
                        subtitle={cfg.subtitle}
                        action={cfg.showViewAllButton ? { to: '/official', label: 'Hamısına bax' } : undefined}
                        className='!mb-3'
                    />

                    {allItems.length > 0 ? (
                        <>
                            {/* filtr pill-ləri + karusel oxları */}
                            <div className='mb-3 flex items-center justify-between gap-3'>
                                {cfg.showTabs && availableTabs.length > 2 ? (
                                    <div className='flex flex-wrap items-center gap-1.5'>
                                        {availableTabs.map(([k, label]) => (
                                            <button key={k} onClick={() => setTab(k)}
                                                className={`rounded-full px-3 py-1 text-[11px] font-bold transition-all ${tab === k ? 'bg-brand-600 text-white shadow-sm shadow-brand-600/25' : 'border border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-700'}`}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                ) : <span />}
                                {scrollable && (
                                    <div className='flex shrink-0 gap-1.5 sm:hidden'>
                                        <button type='button' aria-label='Geri' onClick={() => scroll(-1)} disabled={!canPrev} className='flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-35'><FaArrowLeft size={11} /></button>
                                        <button type='button' aria-label='İrəli' onClick={() => scroll(1)} disabled={!canNext} className='flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-35'><FaArrowRight size={11} /></button>
                                    </div>
                                )}
                            </div>

                            {/* premium tile karuseli */}
                            <div
                                ref={railRef}
                                onScroll={updateRail}
                                className={`category-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain scroll-smooth pb-1 pt-0.5 ${items.length < 4 ? 'justify-start' : ''}`}
                            >
                                {items.map(it => <ServiceTile key={it.key} item={it} />)}
                            </div>
                        </>
                    ) : (
                        <div className='rounded-2xl border border-slate-200 bg-white py-8 text-center'>
                            <p className='text-sm font-semibold text-slate-600'>Rəsmi xidmətlər tezliklə əlavə olunacaq.</p>
                            {cfg.showViewAllButton && <Link to='/official' className='mt-1 inline-block text-xs font-bold text-brand-600 hover:underline'>Rəsmi mağazaya bax →</Link>}
                        </div>
                    )}
                </div>
            </div>
        </HomeSection>
    )
}

export default OfficialTopUpSection
